/**
 * PublicLayout — shared nav + footer for all unauthenticated marketing pages
 * (landing, contact, security). Extracted from LandingPage (s92) so every public
 * page reuses one nav/footer with identical brand chrome.
 *
 * Section anchors (How it works / Features / Pricing / For advisors) smooth-scroll
 * to the in-page section when already on "/"; from any other page they navigate to
 * "/" and stash the target in `pendingScroll`, which LandingPage consumes on mount
 * (see consumePendingScroll). This avoids a full page reload.
 *
 * ── CRAWLABLE LINKS (s216, SEO phase 1 item 1) ───────────────────────────────────────
 * 🔴 EVERY NAVIGATION AND FOOTER ITEM IS AN `<a href>`. It used to be a `<button>` with an
 * onClick, which meant the audit measured ZERO anchors on the home page: a crawler landing
 * on "/" had nowhere to go, and the footer's real destinations (/contact, /security,
 * /privacy-policy — they DID exist in code) were invisible because the URL lived in a JS
 * closure instead of an attribute.
 *
 * The anchors keep the SPA behaviour: `onClick` calls `preventDefault()` and routes or
 * smooth-scrolls exactly as before, so nothing reloads. The `href` is what a crawler reads,
 * and it is also what makes middle-click, ⌘-click and "copy link address" work — those were
 * silently broken for every nav item before this change.
 *
 * 🔴 BUTTONS STAY BUTTONS FOR ACTIONS (the ask's rule): Sign in, Get started, Go to your plan
 * and the hamburger are `<button>`. A section anchor gets `href="#id"`; a route gets its path.
 */
import { SAGE, CLAY, WARM, FONT, PRODUCT_NAME } from "@shared/brand";
import { useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Menu, X } from "lucide-react";

export const CONTAINER = "mx-auto w-full max-w-[1160px]";
/** Shared look for a nav/footer LINK. `background:none`/`border:none` are retained so an
 *  `<a>` renders identically to the `<button>` it replaced (no visual diff in this change). */
const navLinkStyle: React.CSSProperties = { fontSize: 15, color: "#514D44", cursor: "pointer", background: "none", border: "none", padding: 0, fontFamily: "inherit", textDecoration: "none" };

// Cross-page section scroll: set by the nav/footer when a section link is clicked
// from a non-landing page; consumed by LandingPage on mount.
let pendingScroll: string | null = null;
export function consumePendingScroll(): string | null {
  const id = pendingScroll;
  pendingScroll = null;
  return id;
}

/**
 * The nav items. `section` smooth-scrolls on "/" and is exposed as `href="#section"`.
 *
 * 🔴 Pricing and For advisors now carry a `route` as well. The `href` a crawler follows is the
 * REAL PAGE (`/pricing`, `/advisors`) — those are the two URLs people search for and partners
 * link to — while the click keeps the existing in-page scroll for a visitor already on "/".
 * A crawler gets a page; a reader gets the smooth scroll they had before.
 */
const NAV_ITEMS: { label: string; section: string; route?: string }[] = [
  { label: "How it works", section: "how-it-works" },
  { label: "Features", section: "features" },
  { label: "Pricing", section: "pricing", route: "/pricing" },
  { label: "For advisors", section: "advisors", route: "/advisors" },
];

export function PublicNav() {
  const [location, navigate] = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  /**
   * Click behaviour for a nav item, kept consistent with the `href` a crawler sees:
   *   - on "/"        → smooth-scroll to the section (what it always did)
   *   - elsewhere     → go to the item's own route when it has one (matching the href),
   *                     otherwise navigate to "/" and stash the scroll target as before.
   */
  const goToSection = (item: { section: string; route?: string }) => (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location === "/") {
      document.getElementById(item.section)?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (item.route) {
      navigate(item.route);
      return;
    }
    pendingScroll = item.section;
    navigate("/");
  };

  /** The `href`: a real route when the item has one, else an in-page anchor that is still
   *  correct from another page ("/#features" rather than a relative "#features"). */
  const hrefFor = (item: { section: string; route?: string }) =>
    item.route ?? (location === "/" ? `#${item.section}` : `/#${item.section}`);
  const go = (path: string) => () => {
    setMenuOpen(false);
    navigate(path);
  };
  const goHome = (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <header style={{ position: "sticky", top: 0, zIndex: 60, background: "rgba(250,247,241,0.82)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
      <div className={`${CONTAINER} flex items-center justify-between gap-6`} style={{ padding: "17px 32px" }}>
        {/* The wordmark is a LINK to home, not a button — it is the primary "up" navigation on
            every marketing page and the strongest internal link on the site. */}
        <a href="/" onClick={goHome} style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: FONT.serif, fontWeight: 500, fontSize: 33, letterSpacing: "0.005em", color: WARM.ink, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "none" }}>
          <svg width="46" height="46" viewBox="0 0 64 64" fill="none" aria-hidden="true" style={{ flex: "none" }}>
            <circle cx="32" cy="32" r="24" stroke={SAGE[600]} strokeWidth="3" />
            <path d="M32 46V24" stroke={SAGE[600]} strokeWidth="3" strokeLinecap="round" />
            <path d="M32 30l-7-5.5M32 30l7-5.5M32 38l-6-4.5M32 38l6-4.5" stroke={SAGE[600]} strokeWidth="3" strokeLinecap="round" />
          </svg>
          {PRODUCT_NAME}
        </a>

        {/* desktop nav */}
        <nav className="hidden min-[720px]:flex items-center" style={{ gap: 30 }}>
          {NAV_ITEMS.map((n) => (
            <a key={n.section} href={hrefFor(n)} onClick={goToSection(n)} style={navLinkStyle}>{n.label}</a>
          ))}
          {isAuthenticated ? (
            <button onClick={go("/dashboard")} style={{ fontSize: 15, color: WARM.white, background: SAGE[600], padding: "10px 18px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit" }}><span className="inline-flex items-center gap-1.5">Go to your plan<ArrowRight className="h-4 w-4" aria-hidden="true" /></span></button>
          ) : (
            <>
              <button onClick={go("/login")} style={{ ...navLinkStyle, color: SAGE[600] }}>Sign in</button>
              <button onClick={go("/signup")} style={{ fontSize: 15, color: WARM.white, background: SAGE[600], padding: "10px 18px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit" }}>Get started</button>
            </>
          )}
        </nav>

        {/* mobile hamburger */}
        <button className="flex min-[720px]:hidden" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu" style={{ background: "none", border: "none", cursor: "pointer", color: WARM.ink }}>
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* mobile menu */}
      {menuOpen && (
        <div className="flex flex-col min-[720px]:hidden" style={{ borderTop: "1px solid rgba(0,0,0,0.06)", background: "rgba(250,247,241,0.97)", padding: "12px 32px 20px", gap: 14 }}>
          {NAV_ITEMS.map((n) => (
            <a key={n.section} href={hrefFor(n)} onClick={goToSection(n)} style={{ ...navLinkStyle, textAlign: "left" }}>{n.label}</a>
          ))}
          {isAuthenticated ? (
            <button onClick={go("/dashboard")} style={{ fontSize: 15, color: WARM.white, background: SAGE[600], padding: "11px 18px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}><span className="inline-flex items-center gap-1.5">Go to your plan<ArrowRight className="h-4 w-4" aria-hidden="true" /></span></button>
          ) : (
            <>
              <button onClick={go("/login")} style={{ ...navLinkStyle, color: SAGE[600], textAlign: "left" }}>Sign in</button>
              <button onClick={go("/signup")} style={{ fontSize: 15, color: WARM.white, background: SAGE[600], padding: "11px 18px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>Get started</button>
            </>
          )}
        </div>
      )}
    </header>
  );
}

type FooterLink = { label: string; section?: string; to?: string };
/**
 * 🔴 EVERY footer item has a destination, and now every destination is in an `href`.
 *
 * The audit reported that Contact / Estate lawyers / Trust companies / Advisors were "buttons
 * whose destination could not be found from the home page". Measured s216: the destinations DID
 * exist in this array all along (`/contact`, `/security`, `/privacy-policy`) — they were
 * unreachable to a crawler because the path lived in an onClick closure, not an attribute. So
 * this is not "add the missing destinations", it is "expose the ones that were already here".
 *
 * Pricing and Advisors now point at their own ROUTES rather than the landing page's anchors,
 * which is what makes them linkable from outside the site.
 */
const FOOTER_COLS: { heading: string; links: FooterLink[] }[] = [
  { heading: "Product", links: [{ label: "Features", section: "features" }, { label: "Pricing", to: "/pricing" }, { label: "How it works", section: "how-it-works" }] },
  { heading: "For partners", links: [{ label: "Advisors", to: "/advisors" }, { label: "Estate lawyers", to: "/contact" }, { label: "Trust companies", to: "/contact" }] },
  { heading: "Company", links: [{ label: "Security", to: "/security" }, { label: "Privacy", to: "/privacy-policy" }, { label: "Contact", to: "/contact" }] },
];

export function PublicFooter() {
  const [location, navigate] = useLocation();

  /** A route link keeps its path; a section link resolves to "#id" on "/" and "/#id" elsewhere. */
  const footerHref = (link: FooterLink) =>
    link.to ?? (location === "/" ? `#${link.section}` : `/#${link.section}`);

  const onLink = (link: FooterLink) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (link.to) {
      navigate(link.to);
      return;
    }
    if (link.section) {
      if (location === "/") {
        document.getElementById(link.section)?.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        pendingScroll = link.section;
        navigate("/");
      }
    }
  };

  return (
    <footer style={{ background: WARM.cream, borderTop: "1px solid rgba(0,0,0,0.07)", padding: "56px 32px 40px" }}>
      <div className={`${CONTAINER} grid gap-10 grid-cols-1 min-[560px]:grid-cols-2 min-[860px]:grid-cols-[1.4fr_1fr_1fr_1fr]`}>
        <div>
          <div style={{ fontFamily: FONT.serif, fontSize: 20, letterSpacing: "0.02em", color: WARM.ink, marginBottom: 12 }}>{PRODUCT_NAME}</div>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: WARM.taupe, margin: 0, maxWidth: 240 }}>Estate planning for Canadians — from the first decision to the last form.</p>
        </div>
        {FOOTER_COLS.map((col) => (
          <div key={col.heading}>
            <div style={{ fontSize: 12, fontWeight: 500, letterSpacing: "0.09em", textTransform: "uppercase", color: "#B8AE9B", marginBottom: 14 }}>{col.heading}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {col.links.map((l, i) => (
                <a key={i} href={footerHref(l)} onClick={onLink(l)} style={{ ...navLinkStyle, fontSize: 14.5, textAlign: "left" }}>{l.label}</a>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className={`${CONTAINER} flex justify-between items-center gap-4 flex-wrap`} style={{ margin: "40px auto 0", paddingTop: 24, borderTop: "1px solid rgba(0,0,0,0.06)", fontSize: 13, color: WARM.taupe }}>
        <span>© 2026 {PRODUCT_NAME} · Made in Canada</span>
        <span>Sensitive data stays on your device — never on our servers.</span>
      </div>
    </footer>
  );
}

/** Wraps a public page in the shared nav + footer with the landing brand canvas. */
export function PublicLayout({ children }: { children: ReactNode }) {
  return (
    <div style={{ background: WARM.cream, overflowX: "hidden", fontFamily: FONT.sans, color: WARM.charcoal }}>
      <style>{`::selection{background:${SAGE[100]}}`}</style>
      <PublicNav />
      {children}
      <PublicFooter />
    </div>
  );
}
